import { Dialog, Portal } from "@ark-ui/react";
import { Typography, useTheme, Box } from "@mui/material";
import { useState, ReactNode } from "react";
import Button from "../../../../ui/buttons/Button";

interface CodeModalProps {
  children: ReactNode;
  code: string;
  continueText: string;
  description: string;
  onContinue: () => void;
  title: string;
}

export const CodeModal = ({
  children,
  code,
  continueText,
  description,
  onContinue,
  title,
}: CodeModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();

  const handleContinue = () => {
    setIsOpen(false);
    onContinue();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <div onClick={() => setIsOpen(true)}>{children}</div>
      <Dialog.Root onOpenChange={(e) => setIsOpen(e.open)} open={isOpen}>
        <Portal>
          <Dialog.Backdrop
            style={{
              backdropFilter: "blur(2px)",
              background: "rgba(0, 0, 0, 0.5)",
              bottom: 0,
              left: 0,
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1000,
            }}
          />
          <Dialog.Positioner
            style={{
              alignItems: "center",
              display: "flex",
              height: "100%",
              justifyContent: "center",
              left: 0,
              padding: "1rem",
              position: "fixed",
              top: 0,
              width: "100%",
              zIndex: 1001,
            }}
          >
            <Dialog.Content asChild>
              <Box
                sx={{
                  backgroundColor: theme.palette.white.main,
                  borderRadius: "8px",
                  boxShadow:
                    "0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                  margin: "0",
                  maxHeight: "90vh",
                  maxWidth: "500px",
                  outline: "none",
                  overflow: "auto",
                  padding: "2rem",
                  position: "relative",
                  width: "100%",
                  [theme.breakpoints.down(500)]: {
                    margin: "1rem",
                    maxWidth: "calc(100% - 2rem)",
                    padding: "1.5rem",
                  },
                }}
              >
                <Dialog.Title
                  style={{
                    color: theme.palette.black.main,
                    fontFamily: "Poppins",
                    fontSize: "1.5em",
                    fontWeight: 600,
                    marginBottom: "1rem",
                    textAlign: "center",
                  }}
                >
                  {title}
                </Dialog.Title>
                <Dialog.Description
                  style={{
                    color: theme.palette.gray.dark,
                    fontFamily: "Mulish",
                    fontSize: 16,
                    lineHeight: 1.5,
                    marginBottom: "2rem",
                    textAlign: "center",
                  }}
                >
                  {description}
                </Dialog.Description>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "2rem",
                  }}
                >
                  <div
                    style={{
                      background: "#daeee1",
                      borderRadius: "60% 40% 50% 70% / 45% 65% 55% 75%",
                      padding: "1.5rem 2rem",
                      position: "relative",
                      transform: "rotate(-1.5deg)",
                    }}
                  >
                    <Typography
                      sx={{
                        textAlign: "center",
                        transform: "rotate(1.5deg)",
                      }}
                      variant="h2"
                    >
                      {code}
                    </Typography>
                  </div>
                </div>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "1rem",
                    justifyContent: "center",
                    marginTop: "1rem",
                    [theme.breakpoints.down(500)]: {
                      flexDirection: "column",
                      gap: "0.75rem",
                    },
                  }}
                >
                  <Button
                    fullyColored
                    label={continueText}
                    onClick={handleContinue}
                    variant="contained"
                  />
                </Box>
                <Dialog.CloseTrigger asChild>
                  <button
                    onClick={handleClose}
                    onMouseEnter={(e) => {
                      (e.target as HTMLButtonElement).style.color =
                        theme.palette.gray.dark;
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.color =
                        theme.palette.gray.main;
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: theme.palette.gray.main,
                      cursor: "pointer",
                      fontSize: "1.5rem",
                      position: "absolute",
                      right: "1rem",
                      top: "1rem",
                      transition: "color 0.2s ease",
                    }}
                  >
                    ×
                  </button>
                </Dialog.CloseTrigger>
              </Box>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
};
